import SeoWrapper from 'components/Seo/SeoWrapper';
import VehicleDetailCarouselWrapper from 'components/Vehicle/VehicleDetail/VehicleDetailCarouselWrapper';
import { checkAuthentication } from 'guards/checkAuthentication';
import type { GetServerSideProps, NextPage } from 'next';
import useTranslation from 'next-translate/useTranslation';
import { experienceService } from 'services/ExperienceService';
import { ExperienceDetail } from 'types/experience/experience-detail.type';
import AvatarIcon from 'components/Icons/vehicle/avatar.svg';
import BDivider from 'components/common/BDivider';
import ExperienceDetailGeneralInfo from 'components/Experience/ExperienceDetailGeneralInfo';
import VehicleDetailTitledSection from 'components/Vehicle/VehicleDetail/VehicleDetailTitledSection';
import SuggestedExperiences from 'components/Experience/SuggestedExperiences';
import ExperienceDetailClampTextSection from 'components/Experience/ExperienceDetailClampTextSection';
import ExperienceDetailContact from 'components/Experience/ExperienceDetailContact';
import ExperienceDetailPriceWidget from 'components/Experience/PriceWidget/ExperienceDetailPriceWidget';
import BImage from 'components/common/BImage';
import { DropdownOption } from 'types/types';
import { useEffect, useMemo } from 'react';
import { linkService } from 'services/LinkService';
import { useLocale } from 'hooks/useLocale';
import { useBreadcrumbs } from 'hooks/useBreadcrumbs';
import { useRouter } from 'next-translate-routes';
import BBreadcrumb from 'components/common/BBreadcrumb';
import { HelmetSettings } from 'types/seo.type';
import { Locale } from 'enums/locale';
import { getVehicleHelmetSettings } from 'lib/seo/seoData';
import { analyticsService } from 'services/AnalyticsService';
import { usePrevLink } from 'hooks/usePrevLink';
import { ExperienceSelectAndWishlistEventPayload } from 'types/analytics.type';
import ExperienceDetailTitle from 'components/Experience/ExperienceDetailTitle';
import ExperienceDetailComments from 'components/Experience/ExperienceDetailComments';
import {
  localizationService,
  SlugTranslations,
} from 'services/LocalizationService';

export const getServerSideProps: GetServerSideProps = checkAuthentication(
  async (ctx) => {
    const experience = (await experienceService.getExperienceSSR(
      ctx
    )) as ExperienceDetail;

    if (experience === null) {
      return {
        notFound: true,
      };
    }

    const slugTranslations: SlugTranslations = {
      slug: {
        tr: linkService.getExperienceLink(
          experience.titleInLanguages.tr,
          experience.id,
          Locale.TR,
          false
        ),
        en: linkService.getExperienceLink(
          experience.titleInLanguages.en,
          experience.id,
          Locale.EN,
          false
        ),
      },
    };

    localizationService.slugTranslations = { ...slugTranslations };

    return {
      props: {
        experience,
        slugTranslations,
      },
    };
  }
);

interface ExperienceProps {
  slugTranslations: SlugTranslations;
  experience: ExperienceDetail;
}

const Experience: NextPage<ExperienceProps> = ({
  experience,
  slugTranslations,
}) => {
  localizationService.slugTranslations = { ...slugTranslations };
  const { t } = useTranslation();
  const { locale } = useLocale();
  const {
    asPath,
    query: { slug },
  } = useRouter();

  const { defaultBreadcrumbs, generateCategoryLinks } = useBreadcrumbs();
  const experienceName = experience.title;
  const category: DropdownOption<string> = useMemo(() => {
    return linkService.getCategoryLinkOption(null, locale, 'EXPERIENCE');
  }, [experience]);

  const breadcrumbs = [
    ...defaultBreadcrumbs,
    ...generateCategoryLinks(category, experience.seoLocation),
    {
      label: experienceName,
      link: asPath,
      isTranslatable: false,
    },
  ];

  const { getPrevLink } = usePrevLink();

  useEffect(() => {
    analyticsService.experienceDetailPageView({
      asPathOfPreviousPage: getPrevLink(),
      experienceId: experience.id,
      experienceName: experienceName,
      experienceCategory: experience.type,
      regionTrName: experience.type,
    });
  }, [slug]);

  const helmetSettings: HelmetSettings = useMemo(() => {
    const description = t('experience:seo.description', {
      experienceName,
      id: experience.id,
    });
    const localePrefix =
      locale === Locale.EN ? 'en/boat-tours' : 'tekne-turlari';
    const url = `https://www.teknevia.com/${localePrefix}/${slug}`;
    return getVehicleHelmetSettings(
      experienceName,
      description,
      locale as Locale,
      url,
      experience.images[0],
      {
        images: [...experience.images],
        commentCount: experience.ratingAverageBundle.reviewCount || 0,
        ratingCount: experience.ratingAverageBundle.reviewCount || 0,
        name: experienceName,
        description: description,
        category: t('experience:seo.productCategory'),
        rating: Number(
          (experience.ratingAverageBundle.overallAverage || 0).toFixed(2)
        ),
        url,
      }
    );
  }, [t, locale]);

  const addToWishlistPayload: ExperienceSelectAndWishlistEventPayload = useMemo(
    () => ({
      experienceId: experience.id,
      experienceName: experienceName,
      experienceCategory: experience.type,
      regionTrName: experience.regionTRName,
      asPathOfPreviousPage: getPrevLink(),
    }),
    [experience, asPath, experienceName, t]
  );

  return (
    <SeoWrapper helmetSettings={helmetSettings}>
      <article className="vehicle-detail touch-pan-y">
        <BBreadcrumb breadcrumbItems={breadcrumbs} wrapperClasses="mb-4" />
        <ExperienceDetailTitle
          experienceName={experienceName}
          harbor={experience.harbor.label}
          region={experience.region.label}
          isFavorited={experience.isFavorited}
          experienceId={experience.id}
          addToWishlistPayload={addToWishlistPayload}
        />
        <VehicleDetailCarouselWrapper
          images={experience.images}
          vehicleName={experienceName}
          wrapperClass="my-6"
        />
        <div className="mt-8 flex w-full justify-between gap-6">
          <section className="w-full space-y-8 md:w-[70%]">
            <div>{experience.description}</div>
            <BDivider className="my-8 w-full" />
            <div className="flex justify-between">
              <div>
                <div className="mb-3">
                  {t('experience:ownerSection.type.label')}:{' '}
                  {t(`experience:ownerSection.type.${experience.type}`)}
                </div>
                <div>
                  {t('experience:ownerSection.ownerLabel')}:{' '}
                  {experience.partner.name}
                </div>
              </div>
              <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full">
                {experience.partner.avatar ? (
                  <BImage
                    src={experience.partner.avatar}
                    alt={experience.partner.name}
                    objectFit="cover"
                    layout="fixed"
                    width={48}
                    height={48}
                  />
                ) : (
                  <AvatarIcon size={48} />
                )}
              </div>
            </div>
            <BDivider className="my-8 w-full" />
            <ExperienceDetailGeneralInfo
              duration={experience.duration}
              cancellationPolicy={experience.cancellationPolicy}
              supportedLanguages={experience.supportedLanguages}
              harbor={experience.harbor.label}
            />
            <BDivider className="my-8 w-full" />
            <ExperienceDetailClampTextSection
              title={t('experience:highlights.title')}
              description={experience.importantPoints}
            />
            <BDivider className="my-8 w-full" />
            <ExperienceDetailClampTextSection
              title={t('experience:fullDescription.title')}
              description={experience.fullDescription}
            />
            <BDivider className="my-8 w-full" />
            <ExperienceDetailClampTextSection
              title={t('experience:whatsIncluded.title')}
              description={experience.whatsIncluded}
            />
            <BDivider className="my-8 w-full" />
            <ExperienceDetailClampTextSection
              title={t('experience:neededInfo.title')}
              description={experience.neededInfo}
            />
            <BDivider className="my-8 w-full" />
            <ExperienceDetailClampTextSection
              title={t('experience:notSuitableFor.title')}
              description={experience.notSuitableFor}
            />
            <BDivider className="my-8 w-full" />
            <ExperienceDetailClampTextSection
              title={t('experience:routeDescription.title')}
              description={experience.routeDescription}
            />
            {/* <BDivider className="my-8 w-full" /> */}
            {/* <GoogleMapsCircleMap value={JSON.parse(experience.location)} /> */}
            <BDivider className="my-8 w-full" />
            <ExperienceDetailContact />
            <BDivider className="my-8 w-full" />
          </section>
          <ExperienceDetailPriceWidget experience={experience} />
        </div>
        <ExperienceDetailComments experience={experience} />
        <VehicleDetailTitledSection
          title={t('experience:similarExperiences.title')}
          noHeading
          className="mt-8"
        >
          <SuggestedExperiences experiences={experience.similarExperiences} />
        </VehicleDetailTitledSection>
      </article>
    </SeoWrapper>
  );
};

export default Experience;
